import React, { useState, useEffect } from 'react'
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  message,
  Space,
  Tag,
 Spin,
  Alert,
  Checkbox
} from 'antd'
import {
  LinkOutlined,
  TagOutlined,
  FolderOutlined,
  PlusOutlined,
  ToolOutlined
} from '@ant-design/icons'
import { api } from '../services/api'

const { TextArea } = Input
const { Option } = Select

interface AddBookmarkModalProps {
  visible: boolean
  onCancel: () => void
  onSuccess: () => void
}

interface Tag {
  id: number
  name: string
  color?: string
}

interface Folder {
  id: number
  name: string
}

const AddBookmarkModal: React.FC<AddBookmarkModalProps> = ({
  visible,
  onCancel,
  onSuccess
}) => {
  const [form] = Form.useForm()
 const [loading, setLoading] = useState(false)
 const [extracting, setExtracting] = useState(false)
 const [tags, setTags] = useState<Tag[]>([])
  const [analyzing, setAnalyzing] = useState(false)
  const [autoAnalyze, setAutoAnalyze] = useState(false)
 const [folders, setFolders] = useState<Folder[]>([])
  const [extractedData, setExtractedData] = useState<any>(null)

  useEffect(() => {
    if (visible) {
      loadTags()
      loadFolders()
      form.resetFields()
      setExtractedData(null)
    }
  }, [visible])

  const loadTags = async () => {
    try {
      const response = await api.tags.list()
      if (response.success) {
        setTags(response.data.items || [])
      }
    } catch (error) {
      console.error('加载标签失败:', error)
    }
  }

  const loadFolders = async () => {
    try {
      const response = await api.folders.list()
      if (response.success) {
        setFolders(response.data || [])
      }
    } catch (error) {
      console.error('加载文件夹失败:', error)
    }
  }

  const handleUrlExtract = async (url: string) => {
    if (!url || !url.startsWith('http')) {
      return
    }

    try {
      setExtracting(true)
      // 这里调用后端的URL内容提取API
      const response = await api.articles.extractUrl(url)
      if (response.success) {
        const data = response.data
        setExtractedData(data)
        
        // 自动填充表单
        form.setFieldsValue({
          title: data.title || '',
          summary: data.summary || '',
          content: data.content || ''
        })
        
        message.success('网页内容提取成功')
      } else {
        message.warning('无法提取网页内容，请手动填写')
      }
    } catch (error) {
      console.error('提取网页内容失败:', error)
      message.warning('网页内容提取失败，请手动填写')
    } finally {
      setExtracting(false)
    }
  }

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true)
      
      const articleData = {
        url: values.url,
        title: values.title,
        content: values.content || '',
        summary: values.summary || '',
        tags: values.tags || [],
        folder_id: values.folder_id
      }

      const response = await api.articles.create(articleData)
     
      if (response.success) {
        const articleId = response.data?.id
        if (autoAnalyze && articleId) {
          setAnalyzing(true)
          message.loading({ content: '收藏成功，正在分析并记入工具库…', key: 'analyze', duration: 0 })
          try {
            const toolResp: any = await api.tools.fromArticle(articleId, false)
            if (toolResp.success) {
              message.success({ content: `收藏成功，已分析为工具：${toolResp.data?.name || ''}`, key: 'analyze', duration: 4 })
            } else {
              message.warning({ content: '收藏成功，但分析为工具失败', key: 'analyze', duration: 4 })
            }
          } catch (err) {
            console.error('分析为工具失败:', err)
            message.warning({ content: '收藏成功，但分析为工具失败', key: 'analyze', duration: 4 })
          } finally {
            setAnalyzing(false)
          }
        } else {
          message.success('收藏成功！')
        }
        form.resetFields()
        setExtractedData(null)
        onSuccess()
        onCancel()
      }
    } catch (error: any) {
      console.error('收藏失败:', error)
      if (error.response?.status === 409) {
        message.error('该URL已经收藏过了')
      } else {
        message.error('收藏失败，请重试')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title={
        <Space>
          <LinkOutlined />
          添加收藏
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Form.Item
          label="网页URL"
          name="url"
          rules={[
            { required: true, message: '请输入网页URL' },
            { type: 'url', message: '请输入有效的URL' }
          ]}
        >
          <Input
            placeholder="https://example.com/article"
            prefix={<LinkOutlined />}
            onBlur={(e) => handleUrlExtract(e.target.value)}
            suffix={
              extracting && <Spin size="small" />
            }
          />
        </Form.Item>

        {extracting && (
          <Alert
            message="正在提取网页内容..."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {extractedData && (
          <Alert
            message="网页内容提取成功，您可以编辑以下信息"
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Form.Item
          label="标题"
          name="title"
          rules={[{ required: true, message: '请输入文章标题' }]}
        >
          <Input placeholder="文章标题" />
        </Form.Item>

        <Form.Item
          label="摘要"
          name="summary"
        >
          <TextArea
            placeholder="文章摘要（可选）"
            rows={3}
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Form.Item
          label="内容"
          name="content"
        >
          <TextArea
            placeholder="文章内容（可选，如果为空将自动抓取）"
            rows={6}
            maxLength={10000}
            showCount
          />
        </Form.Item>

        <Form.Item
          label="标签"
          name="tags"
        >
          <Select
            mode="multiple"
            placeholder="选择或创建标签"
            allowClear
            style={{ width: '100%' }}
          >
            {tags.map(tag => (
              <Option key={tag.id} value={tag.name}>
                <Tag color={tag.color}>{tag.name}</Tag>
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="文件夹"
          name="folder_id"
        >
          <Select
            placeholder="选择文件夹（可选）"
            allowClear
            style={{ width: '100%' }}
          >
            {folders.map(folder => (
              <Option key={folder.id} value={folder.id}>
                <FolderOutlined style={{ marginRight: 8 }} />
                {folder.name}
              </Option>
           ))}
         </Select>
       </Form.Item>

        <Form.Item style={{ marginBottom: 16 }}>
          <Checkbox
            checked={autoAnalyze}
            onChange={(e) => setAutoAnalyze(e.target.checked)}
          >
            <Space>
              <ToolOutlined />
              收藏后自动分析并记入工具库
            </Space>
          </Checkbox>
        </Form.Item>

       <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
         {analyzing && <Spin size="small" style={{ marginRight: 8 }} />}
         <Space>
           <Button onClick={onCancel}>
             取消
           </Button>
           <Button
             type="primary"
             htmlType="submit"
              loading={loading || analyzing}
             icon={<PlusOutlined />}
           >
             添加收藏
           </Button>
         </Space>
       </Form.Item>
      </Form>
    </Modal>
  )
}

export default AddBookmarkModal
